
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "../../utills/api";
import { io } from "socket.io-client";

export const NotificationContext = createContext();
export const useNotifications = () => useContext(NotificationContext);

// ---------------- ENV DETECTION ----------------
// Detect if running in dev (localhost) or production (any admin subdomain)
const isLocal = window.location.hostname === "localhost";

// Socket URL
const SOCKET_URL = isLocal
  ? "http://localhost:5000"        // only for local dev
  : `${window.location.protocol}//${window.location.hostname}`; // use current domain in production

// API Base URL (axios already handles /api in production)
const API_BASE = isLocal ? "http://localhost:5000/api" : "/api";


export const NotificationProvider = ({ children, userId }) => {
  const [notifications, setNotifications] = useState([]);
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);
  const [globalPopup, setGlobalPopup] = useState({
    show: false,
    message: "",
  });

  const audioRef = useRef(new Audio("/notification.mp3"));

  // ---------------- SOCKET CONNECTION ----------------
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ["websocket"],
    });

    setSocket(newSocket);
    return () => newSocket.disconnect();
  }, []);

  // ---------------- REAL-TIME LISTENER ----------------
  useEffect(() => {
    if (!socket) return;

    socket.on("newNotification", (notification) => {
      if (
        notification.userId === userId ||
        notification.toAdmins ||
        (!notification.userId && !notification.toAdmins)
      ) {
        handleIncomingNotification({ ...notification, isNew: true });
      }
    });

    return () => socket.off("newNotification");
  }, [socket, userId]);

  // ---------------- FETCH EXISTING NOTIFICATIONS ----------------
const fetchNotifications = async () => {
  try {
    setLoading(true);

    const res = await axios.get(`${API_BASE}/notification`, {
      params: { userId },
    });

    const data = Array.isArray(res.data)
      ? res.data
      : res.data.notifications || res.data.data || [];

    setNotifications(data);

  } catch (err) {
    console.error("Error fetching notifications:", err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchNotifications();
  }, [userId]);

  // ---------------- ADD LOCAL NOTIFICATION ----------------
  const addNotification = useCallback(({ title, message, type }) => {
    const newNotification = {
      _id: Date.now(),
      title,
      message,
      type,
      isNew: true,
      createdAt: new Date(),
    };

    handleIncomingNotification(newNotification);
  }, []);

  // ---------------- HANDLE INCOMING ----------------
  const handleIncomingNotification = (notification) => {
    setNotifications((prev) =>
  Array.isArray(prev) ? [notification, ...prev] : [notification]
);

    if (notification.isNew) {
      showPopup(notification);
      showGlobalPopup(notification);
      playSound();
    }
  };

  // ---------------- SMALL POPUP ----------------
  const showPopup = (notification) => {
    const popup = {
      _id: notification._id || Date.now(),
      title: notification.title,
      message: notification.message,
      createdAt: notification.createdAt || new Date(),
    };

    setPopups((prev) => [popup, ...prev]);

    setTimeout(() => dismissPopup(popup._id, false), 5000);
  };

  const dismissPopup = (popupId, markRead = false) => {
    setPopups((prev) => prev.filter((p) => p._id !== popupId));
    if (markRead) markAsRead(popupId);
  };

  // ---------------- GLOBAL POPUP ----------------
  const showGlobalPopup = (notification) => {
    setGlobalPopup({
      show: true,
      message:
        notification.message ||
        notification.title ||
        "You have a new notification",
    });

    setTimeout(() => {
      setGlobalPopup({ show: false, message: "" });
    }, 5000);
  };

  const closeGlobalPopup = () => {
    setGlobalPopup({ show: false, message: "" });
  };

  // ---------------- SOUND ----------------
  const playSound = () => {
    try {
      audioRef.current.play().catch(() => {
        console.warn("User interaction required to play sound");
      });
    } catch (err) {
      console.error("Error playing sound:", err);
    }
  };

  const unreadCount = Array.isArray(notifications)
  ? notifications.filter((n) => !n.read).length
  : 0;

  // ---------------- MARK AS READ ----------------
  const markAsRead = async (notificationId) => {
    try {
      setNotifications((prev) =>
        prev.filter((n) => n._id !== notificationId)
      );

      await axios.patch(`${API_BASE}/notification/${notificationId}/read`, {
        read: true,
      });
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  // ---------------- CLEAR ALL ----------------
  const deleteAllNotifications = async () => {
    try {
      await axios.delete(`${API_BASE}/notification/all`);
      setNotifications([]);
    } catch (err) {
      console.error("Error deleting all notifications", err);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        markAsRead,
        deleteAllNotifications,
        popups,
        dismissPopup,
        unreadCount,
        globalPopup,
        closeGlobalPopup,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
